'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { getCountForTier, LOD_LEVELS } from '@/utils/point-generation'

interface GlobeControlsProps {
	pointSize: number
	autoRotate: boolean
	coreSize: number
	currentTier: number
	cameraDistance: number
	viewMode: 'globe' | 'grid'
	onPointSizeChange: (value: number) => void
	onAutoRotateChange: (value: boolean) => void
	onCoreSizeChange: (value: number) => void
	onViewModeChange: (value: 'globe' | 'grid') => void
}

function formatCount(val: number) {
	if (val >= 1000000) return (val / 1000000).toFixed(0) + 'M'
	if (val >= 1000) return (val / 1000).toFixed(0) + 'K'
	return val
}

export function GlobeControls({
	pointSize,
	autoRotate,
	coreSize,
	currentTier,
	cameraDistance,
	viewMode,
	onPointSizeChange,
	onAutoRotateChange,
	onCoreSizeChange,
	onViewModeChange
}: GlobeControlsProps) {
	const visiblePoints = getCountForTier(currentTier)

	return (
		<Card className="absolute top-4 right-4 w-72 bg-background/90 backdrop-blur-sm border-border z-10">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium text-foreground">Globe Settings</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* LOD Info */}
				<div className="space-y-3 pb-3 border-b border-border">
					<div className="bg-white/5 p-2 rounded space-y-1">
					<div className="flex justify-between text-[10px]">
						<span className="text-muted-foreground">Visible Points:</span>
						<span className="font-mono">{formatCount(visiblePoints)}</span>
					</div>
					<div className="flex justify-between text-[10px]">
						<span className="text-muted-foreground">Current Tier:</span>
						<span className="font-mono">
							{currentTier} ({LOD_LEVELS[currentTier]?.label})
						</span>
					</div>
					<div className="flex justify-between text-[10px]">
						<span className="text-muted-foreground">Cam Distance:</span>
						<span className="font-mono">{cameraDistance.toFixed(2)}</span>
					</div>
					</div>
				</div>

				<div className="space-y-2">
					<div className="flex justify-between items-center">
						<Label htmlFor="point-size" className="text-xs text-muted-foreground">
							Point Size
						</Label>
						<span className="text-xs font-mono">{pointSize.toFixed(1)}</span>
					</div>
					<Slider
						id="point-size"
						min={0.1}
						max={5}
						step={0.1}
						value={[pointSize]}
						onValueChange={([value]) => onPointSizeChange(value)}
					/>
				</div>

				<div className="flex items-center justify-between py-1">
					<Label htmlFor="view-mode" className="text-xs text-muted-foreground">
						Grid View
					</Label>
					<Switch
						id="view-mode"
						checked={viewMode === 'grid'}
						onCheckedChange={(checked) => onViewModeChange(checked ? 'grid' : 'globe')}
					/>
				</div>

				<div className="flex items-center justify-between py-1">
					<Label htmlFor="auto-rotate" className="text-xs text-muted-foreground">
						Auto Rotate
					</Label>
					<Switch id="auto-rotate" checked={autoRotate} onCheckedChange={onAutoRotateChange} />
				</div>

				<div className="space-y-2">
					<div className="flex justify-between items-center">
						<Label htmlFor="core-size" className="text-xs text-muted-foreground">
							Core Size
						</Label>
						<span className="text-xs font-mono">
							{(coreSize * 100).toFixed(0)}%
						</span>
					</div>
					<Slider
						id="core-size"
						min={0}
						max={0.95}
						step={0.05}
						value={[coreSize]}
						onValueChange={([value]) => onCoreSizeChange(value)}
					/>
				</div>
			</CardContent>
		</Card>
	)
}
